import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  Printer, Save, Loader2, CheckCircle2, AlertCircle, RotateCcw,
  Shield, KeyRound, Copy, Eye, EyeOff, RefreshCw, Wifi, ShieldAlert, Download} from 'lucide-react';

// Backend field keys (never translated) -> i18n subkeys. Object key order drives display order.
const FIELD_LABEL_KEYS = {
  client_name: 'fieldClientName',
  client_phone: 'fieldClientPhone',
  device_lock: 'fieldDeviceLock',
  model: 'fieldModel',
  parts: 'fieldParts',
  date: 'fieldDate',
  price: 'fieldPrice',
  ref: 'fieldRef',
};

const DEFAULT_FIELDS = {
  client_name: true, client_phone: true, device_lock: true, model: true,
  parts: true, date: true, price: true, ref: true,
};

const AdminPrinterSettingsPage = () => {
  const { api } = useAuth();
  const { t, formatDate } = useLanguage();

  // Phase 2.4.4 — only the 50x30mm Bixolon format is supported.
  const SIZE_OPTIONS = [
    { value: '50x30mm', label: t('adminPrinter.size50x30') },
  ];

  const PROTOCOL_OPTIONS = [
    { value: 'ZPL', label: t('adminPrinter.protocolZpl') },
    { value: 'BPL', label: t('adminPrinter.protocolBpl') },
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // /api/admin/printer-config (label rendering)
  const [renderCfg, setRenderCfg] = useState({
    fields: { ...DEFAULT_FIELDS }, font_size: 9, margin: 4, default_size: '50x30mm',
  });
  // /api/printer/settings (network + agent)
  const [netCfg, setNetCfg] = useState({
    name: 'LADERPRINTER', ip: '192.168.0.191', port: 9100,
    subnet: '255.255.255.0', gateway: '192.168.0.1', dns: '192.168.0.1',
    ip_method: 'manual', protocol: 'ZPL', label_format: '50x30mm', active: true,
    has_agent_token: false, agent_token_preview: null, last_test_at: null,
  });
  const [ticketCfg, setTicketCfg] = useState({
    ip: '', port: 9100, protocol: 'ESC/POS',
  });

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [previewSize, setPreviewSize] = useState('50x30mm');
  const [testRef, setTestRef] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);
  const [downloadingAgentExe, setDownloadingAgentExe] = useState(false);
  const [downloadingDesktop, setDownloadingDesktop] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState({
    agentPack: '',
    agentExe: '',
    desktopApp: '',
  });
  const [revealedToken, setRevealedToken] = useState(null);  // shown ONCE after rotate
  const [showTokenClear, setShowTokenClear] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r1, r2] = await Promise.all([
        api.get('/admin/printer-config'),
        api.get('/printer/settings'),
      ]);
      const r = r1?.data || {};
      setRenderCfg({
        fields: { ...DEFAULT_FIELDS, ...(r.fields || {}) },
        font_size: r.font_size ?? 9,
        margin: r.margin ?? 4,
        default_size: r.default_size || '50x30mm',
      });
      const n = r2?.data || {};
      setNetCfg((prev) => ({ ...prev, ...n }));
      setTicketCfg({
        ip: n.ticket_ip ?? '',
        port: n.ticket_port ?? 9100,
        protocol: n.ticket_protocol || 'ESC/POS',
      });
      setPreviewSize(n.label_format || r.default_size || '50x30mm');

      const [packLink, exeLink, desktopLink] = await Promise.allSettled([
        api.get('/printer/agent/pack-link'),
        api.get('/printer/agent/exe-link'),
        api.get('/desktop/app/download-link'),
      ]);
      setDownloadLinks({
        agentPack: packLink.status === 'fulfilled' ? (packLink.value?.data?.url || '') : '',
        agentExe: exeLink.status === 'fulfilled' ? (exeLink.value?.data?.url || '') : '',
        desktopApp: desktopLink.status === 'fulfilled' ? (desktopLink.value?.data?.url || '') : '',
      });
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminPrinter.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleField = (key) => setRenderCfg((c) => ({ ...c, fields: { ...c.fields, [key]: !c.fields[key] } }));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await Promise.all([
        api.put('/admin/printer-config', {
          fields: renderCfg.fields,
          font_size: Number(renderCfg.font_size) || 9,
          margin: Number(renderCfg.margin) || 4,
          default_size: renderCfg.default_size,
        }),
        api.put('/printer/settings', {
          name: netCfg.name,
          ip: netCfg.ip,
          port: Number(netCfg.port) || 9100,
          subnet: netCfg.subnet,
          gateway: netCfg.gateway,
          dns: netCfg.dns,
          ip_method: netCfg.ip_method,
          protocol: netCfg.protocol,
          label_format: netCfg.label_format,
          ticket_ip: ticketCfg.ip,
          ticket_port: Number(ticketCfg.port) || 9100,
          ticket_protocol: ticketCfg.protocol,
          active: !!netCfg.active,
        }),
      ]);
      setMessage(t('adminPrinter.saved'));
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminPrinter.errSave'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!testRef.trim()) { setError(t('adminPrinter.previewNeedRef')); return; }
    setPreviewing(true);
    setError(null);
    try {
      const res = await api.get(`/devices/${testRef.trim()}/qr-label`, {
        params: { size: previewSize }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminPrinter.errPreview'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendTest = async () => {
    setTesting(true);
    setMessage(null);
    setError(null);
    try {
      await api.post('/printer/test');
      setMessage(t('adminPrinter.testQueued'));
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminPrinter.errTest'));
    } finally {
      setTesting(false);
    }
  };

  const handleRotateToken = async () => {
    if (!window.confirm(t('adminPrinter.confirmRotate'))) return;
    setRotating(true);
    setMessage(null);
    setError(null);
    try {
      const res = await api.post('/printer/settings/regenerate-token');
      setRevealedToken(res?.data?.agent_token || null);
      setShowTokenClear(true);
      setMessage(t('adminPrinter.tokenGenerated'));
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminPrinter.errRotate'));
    } finally {
      setRotating(false);
    }
  };

  const copyToken = async () => {
    if (!revealedToken) return;
    try {
      await navigator.clipboard.writeText(revealedToken);
      setMessage(t('adminPrinter.tokenCopied'));
    } catch (_) {
      setError(t('adminPrinter.copyFailed'));
    }
  };

  // Phase 5 — download the agent pack via authenticated request
  // (a plain <a href> can't carry the Authorization header so the endpoint
  // returns 403 to the browser).
  const handleDownloadAgentPack = async () => {
    if (downloadLinks.agentPack) {
      window.location.assign(downloadLinks.agentPack);
      return;
    }
    setDownloadingPack(true);
    setMessage(null);
    setError(null);
    try {
      const res = await api.get('/printer/agent/pack', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'RemobilesAgentPack.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke later so the browser has time to start the download
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setMessage(t('adminPrinter.packDownloaded'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || t('adminPrinter.errUnknown');
      setError(t('adminPrinter.errDownloadPack', { http: status ? ` (HTTP ${status})` : '', detail }));
    } finally {
      setDownloadingPack(false);
    }
  };

  const handleDownloadDesktopApp = async () => {
    if (downloadLinks.desktopApp) {
      window.location.assign(downloadLinks.desktopApp);
      return;
    }
    setDownloadingDesktop(true);
    setMessage(null);
    setError(null);
    try {
      const res = await api.get('/desktop/app/download-link');
      const url = res?.data?.url;
      if (!url) throw new Error(t('adminPrinter.downloadLinkUnavailable'));
      window.location.assign(url);
      setMessage(t('adminPrinter.downloadStartedHint'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || t('adminPrinter.errUnknown');
      setError(t('adminPrinter.errDownloadApp', { http: status ? ` (HTTP ${status})` : '', detail }));
    } finally {
      setDownloadingDesktop(false);
    }
  };

  const handleDownloadAgentExe = async () => {
    if (downloadLinks.agentExe) {
      window.location.assign(downloadLinks.agentExe);
      return;
    }
    setDownloadingAgentExe(true);
    setMessage(null);
    setError(null);
    try {
      const res = await api.get('/printer/agent/exe', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.microsoft.portable-executable' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'RemobilesAgent_v2.0.7.exe';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setMessage(t('adminPrinter.agentExeDownloaded'));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || t('adminPrinter.errUnknown');
      setError(t('adminPrinter.errDownloadAgent', { http: status ? ` (HTTP ${status})` : '', detail }));
    } finally {
      setDownloadingAgentExe(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto fade-in p-2 sm:p-4">
        <div className="flex items-center gap-3 mb-6">
          <Printer className="w-7 h-7 text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">{t('adminPrinter.pageTitle')}</h1>
            <p className="text-sm text-gray-400">{t('adminPrinter.pageSubtitle')}</p>
          </div>
        </div>

        {/* Security note */}
        <div className="mb-6 bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex gap-3" data-testid="printer-cfg-security-note">
          <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-100">
            <p className="font-semibold mb-1">{t('adminPrinter.securityTitle')}</p>
            <p>{t('adminPrinter.securityBody')} (<a className="underline text-amber-200" href={`http://${netCfg.ip}`} target="_blank" rel="noreferrer">http://{netCfg.ip}</a>). {t('adminPrinter.securityNoInternet')}</p>
          </div>
        </div>

        <div className="mb-6 rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-semibold text-cyan-200">{t('adminPrinter.downloadsTitle')}</p>
            <p className="text-sm text-gray-300">{t('adminPrinter.downloadsDesc')}</p>
          </div>
          <Link
            to="/admin/installateurs"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-colors"
          >
            <Download className="w-4 h-4" />
            {t('adminPrinter.openInstallers')}
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('adminPrinter.loading')}
          </div>
        ) : (
          <div className="space-y-6">
            {message && (
              <div className="text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="printer-cfg-success">
                <CheckCircle2 className="w-4 h-4" /> {message}
              </div>
            )}
            {error && (
              <div className="text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="printer-cfg-error">
                <AlertCircle className="w-4 h-4" /> {String(error)}
              </div>
            )}

            {/* Network & Bixolon */}
            <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 sm:p-5 space-y-4" data-testid="printer-cfg-network-section">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Wifi className="w-5 h-5 text-cyan-400" /> {t('adminPrinter.bixolonSection')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('adminPrinter.fieldPrinterName')} testid="printer-cfg-name" value={netCfg.name} onChange={(v) => setNetCfg({ ...netCfg, name: v })} placeholder="LADERPRINTER" />
                <Field label={t('adminPrinter.fieldIp')} testid="printer-cfg-ip" value={netCfg.ip} onChange={(v) => setNetCfg({ ...netCfg, ip: v })} placeholder="192.168.0.191" />
                <Field label={t('adminPrinter.fieldPortRaw')} testid="printer-cfg-port" type="number" value={netCfg.port} onChange={(v) => setNetCfg({ ...netCfg, port: v })} placeholder="9100" />
                <SelectField label={t('adminPrinter.fieldIpMethod')} testid="printer-cfg-ip-method" value={netCfg.ip_method} onChange={(v) => setNetCfg({ ...netCfg, ip_method: v })} options={[{value:'manual',label:t('adminPrinter.ipMethodManual')},{value:'dhcp',label:t('adminPrinter.ipMethodDhcp')}]} />
                <Field label={t('adminPrinter.fieldSubnet')} testid="printer-cfg-subnet" value={netCfg.subnet} onChange={(v) => setNetCfg({ ...netCfg, subnet: v })} placeholder="255.255.255.0" />
                <Field label={t('adminPrinter.fieldGateway')} testid="printer-cfg-gateway" value={netCfg.gateway} onChange={(v) => setNetCfg({ ...netCfg, gateway: v })} placeholder="192.168.0.1" />
                <Field label={t('adminPrinter.fieldDns')} testid="printer-cfg-dns" value={netCfg.dns} onChange={(v) => setNetCfg({ ...netCfg, dns: v })} placeholder="192.168.0.1" />
                <SelectField label={t('adminPrinter.fieldProtocol')} testid="printer-cfg-protocol" value={netCfg.protocol} onChange={(v) => setNetCfg({ ...netCfg, protocol: v })} options={PROTOCOL_OPTIONS} />
                <SelectField label={t('adminPrinter.fieldDefaultLabelFormat')} testid="printer-cfg-label-format" value={netCfg.label_format} onChange={(v) => setNetCfg({ ...netCfg, label_format: v })} options={SIZE_OPTIONS} />
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 mt-6">
                  <input type="checkbox" data-testid="printer-cfg-active" checked={!!netCfg.active} onChange={(e) => setNetCfg({ ...netCfg, active: e.target.checked })} className="accent-cyan-500" />
                  <span className="text-sm text-gray-200">{t('adminPrinter.printerActive')}</span>
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={handleSendTest} disabled={testing} data-testid="printer-cfg-test-btn"
                  className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center gap-2 disabled:opacity-50">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} {t('adminPrinter.testConnection')}
                </button>
                {netCfg.last_test_at && (
                  <span className="text-xs text-gray-400 self-center">{t('adminPrinter.lastTest', { date: formatDate(netCfg.last_test_at, { dateStyle: 'short', timeStyle: 'short' }) })}</span>
                )}
              </div>
            </section>

            <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 sm:p-5 space-y-4" data-testid="printer-cfg-ticket-section">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Printer className="w-5 h-5 text-emerald-400" /> {t('adminPrinter.epsonSection')}</h2>
              <p className="text-sm text-gray-400">
                {t('adminPrinter.epsonDesc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('adminPrinter.fieldTicketIp')} testid="printer-cfg-ticket-ip" value={ticketCfg.ip} onChange={(v) => setTicketCfg({ ...ticketCfg, ip: v })} placeholder="192.168.0.123" />
                <Field label={t('adminPrinter.fieldTicketPort')} testid="printer-cfg-ticket-port" type="number" value={ticketCfg.port} onChange={(v) => setTicketCfg({ ...ticketCfg, port: v })} placeholder="9100" />
                <SelectField
                  label={t('adminPrinter.fieldTicketProtocol')}
                  testid="printer-cfg-ticket-protocol"
                  value={ticketCfg.protocol}
                  onChange={(v) => setTicketCfg({ ...ticketCfg, protocol: v })}
                  options={[
                    { value: 'ESC/POS', label: t('adminPrinter.ticketProtoEscpos') },
                    { value: 'ZPL', label: t('adminPrinter.ticketProtoZplFallback') },
                  ]}
                />
              </div>
            </section>

            {/* Agent local */}
            <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 sm:p-5 space-y-3" data-testid="printer-cfg-agent-section">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Shield className="w-5 h-5 text-cyan-400" /> {t('adminPrinter.agentSection')}</h2>
              <p className="text-xs text-gray-400">{t('adminPrinter.agentDesc')}</p>

              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {revealedToken && showTokenClear ? (
                    <div>
                      <p className="text-xs text-amber-300 mb-1">{t('adminPrinter.tokenCopyNow')}</p>
                      <code className="text-sm text-emerald-300 break-all" data-testid="printer-cfg-token-clear">{revealedToken}</code>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">{t('adminPrinter.agentTokenLabel')} {netCfg.has_agent_token ? t('adminPrinter.tokenCreated') : t('adminPrinter.tokenNotGenerated')}</p>
                      <code className="text-sm text-gray-200" data-testid="printer-cfg-token-preview">{netCfg.agent_token_preview || t('adminPrinter.noToken')}</code>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {revealedToken && (
                    <>
                      <button type="button" onClick={() => setShowTokenClear((v) => !v)} className="text-xs text-gray-300 hover:text-white flex items-center gap-1">
                        {showTokenClear ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showTokenClear ? t('adminPrinter.hide') : t('adminPrinter.show')}
                      </button>
                      <button type="button" onClick={copyToken} data-testid="printer-cfg-token-copy-btn" className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                        <Copy className="w-3 h-3" /> {t('adminPrinter.copy')}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <button type="button" onClick={handleRotateToken} disabled={rotating} data-testid="printer-cfg-token-rotate-btn"
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2 disabled:opacity-50">
                {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {netCfg.has_agent_token ? t('adminPrinter.regenerateToken') : t('adminPrinter.generateToken')}
              </button>
              {/* Phase 5 — agent local download */}
              <div className="mt-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700">
                <p className="text-xs uppercase tracking-wider text-amber-300 mb-2 font-semibold">{t('adminPrinter.agentWinTitle')}</p>
                <p className="text-sm text-gray-300 mb-3">{t('adminPrinter.agentWinDesc')}</p>
                <div className="mb-3 rounded-lg border border-slate-700 bg-slate-950/30 p-3">
                  <p className="text-xs uppercase tracking-wider text-cyan-300 mb-2 font-semibold">{t('adminPrinter.directLinks')}</p>
                  <div className="flex flex-col gap-2 text-sm">
                    <a
                      href={downloadLinks.desktopApp || '#'}
                      download
                      className={`inline-flex items-center gap-2 ${downloadLinks.desktopApp ? 'text-cyan-300 hover:text-cyan-200' : 'text-gray-500 pointer-events-none'}`}
                    >
                      <Download className="w-4 h-4" /> {t('adminPrinter.dlDesktopApp')}
                    </a>
                    <a
                      href={downloadLinks.agentExe || '#'}
                      download
                      className={`inline-flex items-center gap-2 ${downloadLinks.agentExe ? 'text-cyan-300 hover:text-cyan-200' : 'text-gray-500 pointer-events-none'}`}
                    >
                      <Download className="w-4 h-4" /> {t('adminPrinter.dlAgentExe')}
                    </a>
                    <a
                      href={downloadLinks.agentPack || '#'}
                      download
                      className={`inline-flex items-center gap-2 ${downloadLinks.agentPack ? 'text-cyan-300 hover:text-cyan-200' : 'text-gray-500 pointer-events-none'}`}
                    >
                      <Download className="w-4 h-4" /> {t('adminPrinter.dlAgentPack')}
                    </a>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">{t('adminPrinter.tempLinksNote')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                <button type="button" onClick={handleDownloadAgentExe} disabled={downloadingAgentExe}
                   className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold disabled:opacity-50"
                   data-testid="download-agent-exe-btn">
                  {downloadingAgentExe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {downloadingAgentExe ? t('adminPrinter.downloading') : t('adminPrinter.dlAgentExeBtn')}
                </button>
                <button type="button" onClick={handleDownloadAgentPack} disabled={downloadingPack}
                   className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50"
                   data-testid="download-agent-pack-btn">
                  {downloadingPack ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {downloadingPack ? t('adminPrinter.downloading') : t('adminPrinter.dlAgentPackBtn')}
                </button>
                </div>
              </div>

              <div className="mt-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700">
                <p className="text-xs uppercase tracking-wider text-cyan-300 mb-2 font-semibold">{t('adminPrinter.dlDesktopApp')}</p>
                <p className="text-sm text-gray-300 mb-3">{t('adminPrinter.desktopAppDesc')}</p>
                <button type="button" onClick={handleDownloadDesktopApp} disabled={downloadingDesktop}
                   className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold disabled:opacity-50"
                   data-testid="download-desktop-app-btn">
                  {downloadingDesktop ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {downloadingDesktop ? t('adminPrinter.downloading') : t('adminPrinter.dlDesktopAppBtn')}
                </button>
              </div>
            </section>

            {/* Champs imprimes */}
            <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 sm:p-5 space-y-3">
              <h2 className="text-lg font-semibold text-white">{t('adminPrinter.printedFields')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.keys(FIELD_LABEL_KEYS).map((k) => (
                  <label key={k} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 hover:border-cyan-500 cursor-pointer">
                    <input type="checkbox" data-testid={`printer-cfg-field-${k}`} checked={!!renderCfg.fields[k]} onChange={() => toggleField(k)} className="accent-cyan-500" />
                    <span className="text-sm text-gray-200">{t(`adminPrinter.${FIELD_LABEL_KEYS[k]}`)}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Field label={t('adminPrinter.fieldFontSize')} testid="printer-cfg-font-size" type="number" value={renderCfg.font_size} onChange={(v) => setRenderCfg({ ...renderCfg, font_size: v })} />
                <Field label={t('adminPrinter.fieldMargin')} testid="printer-cfg-margin" type="number" value={renderCfg.margin} onChange={(v) => setRenderCfg({ ...renderCfg, margin: v })} />
              </div>
            </section>

            {/* Preview */}
            <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 sm:p-5 space-y-3">
              <h2 className="text-lg font-semibold text-white">{t('adminPrinter.previewPdf')}</h2>
              <p className="text-xs text-gray-400">{t('adminPrinter.previewDesc')}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="text" placeholder={t('adminPrinter.previewRefPlaceholder')} data-testid="printer-cfg-test-ref" value={testRef} onChange={(e) => setTestRef(e.target.value)} className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white" />
                <select data-testid="printer-cfg-test-size" value={previewSize} onChange={(e) => setPreviewSize(e.target.value)} className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white">
                  {SIZE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button type="button" onClick={handlePreview} disabled={previewing} data-testid="printer-cfg-preview-btn"
                  className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center gap-2 disabled:opacity-50">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} {t('adminPrinter.previewBtn')}
                </button>
              </div>
            </section>

            <div className="flex items-center gap-2">
              <button type="button" onClick={handleSave} disabled={saving} data-testid="printer-cfg-save-btn"
                className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-medium flex items-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('adminPrinter.save')}
              </button>
              <button type="button" onClick={load} disabled={loading} data-testid="printer-cfg-reload-btn"
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-gray-200 flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> {t('adminPrinter.reload')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const Field = ({ label, testid, value, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <label className="block text-sm text-gray-300 mb-1.5">{label}</label>
    <input
      type={type}
      data-testid={testid}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
    />
  </div>
);

const SelectField = ({ label, testid, value, onChange, options }) => (
  <div>
    <label className="block text-sm text-gray-300 mb-1.5">{label}</label>
    <select
      data-testid={testid}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default AdminPrinterSettingsPage;
